import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import ParkIcon from '@mui/icons-material/Park';
import { useTranslation } from 'react-i18next';
import { gameTitle, playerRange, playTime } from '../games/gameFormat';
import BggRatingMugs from '../games/BggRatingMugs';

const coverMarks = {
  circle: CircleOutlinedIcon,
  diamonds: GridViewIcon,
  spark: AutoAwesomeIcon,
  star: AutoAwesomeIcon,
  triangle: ChangeHistoryIcon,
  tree: ParkIcon,
};

const fallbackPalettes = [
  ['#d80014', '#5f0307'],
  ['#f4ad3d', '#7b3a06'],
  ['#7fd08b', '#00623d'],
  ['#12a7aa', '#064a63'],
  ['#b9bd3c', '#3c420f'],
  ['#d77a00', '#541b00'],
  ['#0F4C45', '#062522'],
];

const hashString = (value = '') =>
  String(value || '').split('').reduce((total, char) => total + char.charCodeAt(0), 0);

export const getGamePalette = (game = {}) =>
  game?.palette ||
  fallbackPalettes[hashString(game?.title || game?.name) % fallbackPalettes.length];

const coverSizes = {
  small: {
    width: 64,
    radius: 1.5,
    icon: 28,
    title: '0.62rem',
    meta: '0.55rem',
  },
  medium: {
    width: 144,
    radius: 2,
    icon: 52,
    title: '0.75rem',
    meta: '0.68rem',
  },
};

const GameCover = ({
  game,
  size = 'medium',
  sx,
  className,
  footerStart = null,
  footerEnd,
  footerLayout = 'inline',
  topOverlay = null,
  showBggRating = size !== 'small',
}) => {
  const { i18n, t } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const config = coverSizes[size] || coverSizes.medium;
  const MarkIcon = coverMarks[game?.mark] || AutoAwesomeIcon;
  const image = game?.image || game?.thumbnail;
  const palette = getGamePalette(game);
  const ratingContent = footerEnd ?? (showBggRating ? (
    <BggRatingMugs
      game={game}
      size="small"
      showValue={false}
      sx={{ flex: '0 0 auto' }}
    />
  ) : null);
  const stackedFooter = footerLayout === 'stacked';

  return (
    <Box
      sx={{
        position: 'relative',
        flex: '0 0 auto',
        width: config.width,
        aspectRatio: '3 / 4',
        overflow: 'hidden',
        borderRadius: config.radius,
        color: '#fff8e8',
        background: `linear-gradient(155deg, ${palette[0]} 0%, ${palette[1]} 100%)`,
        boxShadow: '0 14px 24px -18px rgba(45, 26, 11, 0.9)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.32), transparent 46%), radial-gradient(circle at 78% 82%, rgba(0,0,0,0.34), transparent 55%)',
        },
        ...sx,
      }}
      className={className}
    >
      {image ? (
        <Box
          component="img"
          src={image}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <MarkIcon
          sx={{
            position: 'absolute',
            inset: 0,
            m: 'auto',
            fontSize: config.icon,
            opacity: 0.9,
            filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.28))',
          }}
        />
      )}

      {topOverlay ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            insetInline: 0,
            p: size === 'small' ? 1 : 1.25,
            display: 'flex',
            justifyContent: 'flex-end',
            background:
              'linear-gradient(180deg, rgba(4, 29, 26, 0.72), rgba(4, 29, 26, 0.18) 68%, transparent)',
            pointerEvents: 'none',
          }}
        >
          {topOverlay}
        </Box>
      ) : null}

      <Box
        sx={{
          position: 'absolute',
          insetInline: 0,
          bottom: 0,
          p: size === 'small' ? 1 : 1.5,
          pt: size === 'small' ? 2 : 2.5,
          display: 'flex',
          alignItems: stackedFooter ? 'stretch' : 'flex-end',
          flexDirection: stackedFooter ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: stackedFooter ? 0.35 : 1,
          background:
            'linear-gradient(0deg, rgba(4, 29, 26, 0.96), rgba(4, 29, 26, 0.86) 56%, rgba(4, 29, 26, 0.2) 84%, transparent)',
          backdropFilter: 'blur(1px)',
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
        }}
      >
        {footerStart}
        <Box sx={{ minWidth: 0, flex: stackedFooter ? '0 0 auto' : '1 1 auto' }}>
          <Typography
            sx={{
              fontFamily: '"Cinzel", Georgia, serif',
              fontSize: config.title,
              fontWeight: 800,
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(stackedFooter
                ? {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    whiteSpace: 'normal',
                  }
                : {
                    whiteSpace: 'nowrap',
                  }),
            }}
          >
            {gameTitle(game, t, language)}
          </Typography>
          {!stackedFooter ? (
            <Typography
              sx={{
                fontSize: config.meta,
                color: 'rgba(255,248,232,0.82)',
                lineHeight: 1.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {game.players || playerRange(game, t)} - {game.duration || playTime(game, t)}
            </Typography>
          ) : null}
        </Box>
        {stackedFooter ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: config.meta,
                color: 'rgba(255,248,232,0.82)',
                lineHeight: 1.15,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {game.players || playerRange(game, t)} - {game.duration || playTime(game, t)}
            </Typography>
            {ratingContent}
          </Box>
        ) : (
          ratingContent
        )}
      </Box>
    </Box>
  );
};

export default GameCover;
